'use client';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Trophy } from 'lucide-react';
import { AmericanFinalBracketSize } from '@/types/tournament.model';
import { cn } from '@/lib/utils';

export interface AmericanFinalBracketSizeFieldProps {
  value: AmericanFinalBracketSize;
  onChange: (value: AmericanFinalBracketSize) => void;
  disabled?: boolean;
  /** Oculta la opción NONE (ej: más de 5 equipos → múltiples grupos) */
  hideNone?: boolean;
  /** Oculta la opción FINAL (ej: más de 8 equipos → 3+ grupos, generaría bracket incorrecto) */
  hideFinal?: boolean;
  /** Oculta la opción EIGHT (ej: menos de 8 equipos) */
  hideEight?: boolean;
  /** Omite el Card wrapper y usa layout vertical (para modales) */
  hideCard?: boolean;
  /** Shows the NONE option as "Hexagonal" (6 teams: a single round-robin group) */
  noneAsHexagonal?: boolean;
}

const OPTIONS: { value: AmericanFinalBracketSize; label: string; description: string }[] = [
  {
    value: AmericanFinalBracketSize.NONE,
    label: 'Sin cuadro final',
    description: 'Solo fase de grupos. El ranking final define las posiciones.',
  },
  {
    value: AmericanFinalBracketSize.FINAL,
    label: 'Solo final',
    description: 'El 1° de cada grupo disputa una gran final.',
  },
  {
    value: AmericanFinalBracketSize.FOUR,
    label: 'Cuadro de 4',
    description: 'Los 4 mejores equipos disputan un bracket eliminatorio.',
  },
  {
    value: AmericanFinalBracketSize.EIGHT,
    label: 'Cuadro de 8',
    description: 'Los 8 mejores equipos disputan un bracket eliminatorio.',
  },
];

// With 6 teams NONE is the Hexagonal: same value, different name for the manager.
const OPTIONS_WITH_HEXAGONAL = OPTIONS.map((opt) =>
  opt.value === AmericanFinalBracketSize.NONE
    ? {
        ...opt,
        label: 'Hexagonal',
        description:
          'Un solo grupo de 6, todos contra todos. Gana el primero de la tabla, sin eliminatorias.',
      }
    : opt,
);

export function AmericanFinalBracketSizeField({
  value,
  onChange,
  disabled = false,
  hideNone = false,
  hideFinal = false,
  hideEight = false,
  hideCard = false,
  noneAsHexagonal = false,
}: AmericanFinalBracketSizeFieldProps) {
  const options = noneAsHexagonal ? OPTIONS_WITH_HEXAGONAL : OPTIONS;
  const visibleOptions = options.filter((opt) => {
    if (opt.value === AmericanFinalBracketSize.NONE && hideNone) return false;
    if (opt.value === AmericanFinalBracketSize.FINAL && hideFinal) return false;
    if (opt.value === AmericanFinalBracketSize.EIGHT && hideEight) return false;
    return true;
  });

  const buttons = visibleOptions.map((opt) => {
    const isSelected = value === opt.value;
    return (
      <button
        key={opt.value}
        type="button"
        onClick={() => !disabled && onChange(opt.value)}
        disabled={disabled}
        className={cn(
          'flex flex-col rounded-lg border-2 p-4 text-left transition-all',
          hideCard ? 'w-full' : '',
          isSelected
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-200 bg-white hover:border-gray-300',
        )}
      >
        <span className={cn('font-medium', isSelected ? 'text-blue-700' : 'text-gray-900')}>
          {opt.label}
        </span>
        <span className={cn('mt-1 text-xs', isSelected ? 'text-blue-700/85' : 'text-muted-foreground')}>
          {opt.description}
        </span>
      </button>
    );
  });

  if (hideCard) {
    return <div className="flex flex-col gap-2">{buttons}</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Trophy className="h-5 w-5" />
          Cuadro final
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Definí si el americano termina en la fase de grupos o incluye una instancia eliminatoria final.
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid gap-3 sm:grid-cols-4">{buttons}</div>
      </CardContent>
    </Card>
  );
}
